import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { authService } from "../../services/auth.service";
import { formatTime } from "../../utils/dateTime.utils";
import { TokenService } from "../../services/token.service";
import { AlarmClock } from "lucide-react";
import Modal from "./Modal";
import { AsyncButton } from "../AsyncButton";

const IDLE_WARNING_THRESHOLD_MS: number = 5 * 60 * 1000; // 5 minutes before timeout
const IDLE_TIMEOUT_MS: number =
  parseInt(import.meta.env.VITE_SESSION_IDLE_TIMEOUT_MINUTES || "30", 10) *
  60 *
  1000;

export const SessionTimeoutModal: React.FC = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const lastActivityRef = useRef<number>(Date.now());
  const isPublicRoute =
    location.pathname === "/" ||
    location.pathname === "/welcome" ||
    location.pathname === "/auth/callback" ||
    location.pathname === "/faq" ||
    location.pathname === "/privacy-policy" ||
    location.pathname === "/terms-of-service" ||
    location.pathname === "/cookie-policy" ||
    location.pathname === "/accessibility-statement";

  // Interpret `VITE_SESSION_IDLE_TIMEOUT_MINUTES` as "time until the modal opens".
  // Once opened, we logout after the fixed countdown duration (5 minutes).
  const MODAL_COUNTDOWN_SECONDS: number = Math.max(
    0,
    Math.floor(IDLE_WARNING_THRESHOLD_MS / 1000),
  );

  // Track activity
  useEffect(() => {
    const events: string[] = ["mousedown", "keydown", "touchstart"];
    const handleActivity = (): void => {
      lastActivityRef.current = Date.now();
    };

    events.forEach((event: string): void => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return (): void => {
      events.forEach((event: string): void =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, []);

  // Check idle time
  useEffect(() => {
    if (isVisible || isPublicRoute || !TokenService.isInternalTokenValid()) {
      return;
    }

    const intervalId: number = setInterval((): void => {
      const idleTime: number = Date.now() - lastActivityRef.current;
      if (idleTime >= IDLE_TIMEOUT_MS) {
        // Always start a fixed countdown, independent of how long we've been idle.
        setRemainingSeconds(MODAL_COUNTDOWN_SECONDS);
        setIsVisible(true);
      }
    }, 1000);

    return (): void => clearInterval(intervalId);
  }, [isVisible, isPublicRoute]);

  // Update countdown
  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const intervalId: number = setInterval((): void => {
      setRemainingSeconds((prevState: number): number => {
        const nextState: number = Math.max(0, prevState - 1);
        if (nextState === 0) {
          setIsVisible(false);
          void authService.logout("inactivity");
        }
        return nextState;
      });
    }, 1000);

    return (): void => clearInterval(intervalId);
  }, [isVisible]);

  return !isVisible ? null : (
    <Modal isOpen={isVisible}>
      <div className="text-center mb-3">
        <AlarmClock size={55} className="mb-3" />

        <h2 id="session-timeout-title" className="fs-4 mb-3">
          Your session is about to expire due to inactivity
        </h2>

        <p className="fs-5 text-dark-emphasis m-0">Logging out in:</p>

        <span className="fs-1 fw-bold text-danger">
          {formatTime(remainingSeconds)}
        </span>
      </div>

      <div className="row gx-3 gy-2 justify-content-center align-items-center">
        <div className="col-12 col-sm-6 col-md-5">
          <AsyncButton
            type="button"
            className="btn btn-outline-danger rounded-pill w-100"
            onClick={(): Promise<void> => authService.logout("inactivity")}
            aria-label="Log out now"
          >
            Log Out Now
          </AsyncButton>
        </div>
        <div className="col-12 col-sm-6 col-md-5">
          <AsyncButton
            type="button"
            className="btn btn-primary w-100"
            onClick={(): void => {
              setIsVisible(false);
              lastActivityRef.current = Date.now();
            }}
            aria-label="Stay logged in and continue session"
          >
            Stay Logged In
          </AsyncButton>
        </div>
      </div>
    </Modal>
  );
};
